import { Box, Dropdown, FieldText, Text } from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { elevioActions } from '../../../../elevio.actions';
import { elevioSelectors } from '../../../../elevio.reducer/selectors';
import { useSearchArticlesInResolution } from '../../../../hooks';
import { transformResolutionSearchArticles } from '../../../../utils/suggestions';
import styles from '../ResolutionModalBody.module.scss';

import { ResolutionArticleList } from './ResolutionArticleList';

export function ResolutionArticleSelect(): JSX.Element {
  const dispatch = useAppDispatch();
  const articleDropdownOpen = useAppSelector(
    elevioSelectors.resolution.articleDropdownOpen,
  );
  const articleSearchTerm = useAppSelector(
    elevioSelectors.resolution.articleSearchTerm,
  );
  const articleSearchInputValue = useAppSelector(
    elevioSelectors.resolution.articleSearchInputValue,
  );
  const selectedArticleId = useAppSelector(
    elevioSelectors.resolution.selectedArticleId,
  );

  const setSearchTerm = (term) =>
    dispatch(
      elevioActions.resolution.setResolutionInputValue({
        resolutionInputValue: term,
      }),
    );

  const { data, error, isFetching } = useSearchArticlesInResolution({
    searchTerm: articleSearchTerm,
  });

  const suggestions = useMemo(
    () => transformResolutionSearchArticles(data),
    [data],
  );
  const selectedSuggestion = suggestions.find(
    (suggestion) => suggestion.id === selectedArticleId,
  );
  const fieldValue = !articleDropdownOpen
    ? selectedSuggestion?.title || articleSearchInputValue
    : articleSearchInputValue;

  return (
    <Box>
      <Box>
        <Text.Paragraph subdued={true} className={styles.paragraph}>
          Which article solved the issue?
        </Text.Paragraph>
        <Box>
          <FieldText
            onKeyUp={(e) => {
              // handle 'escape' keypress
              if (e.keyCode === 27) {
                if (articleDropdownOpen) {
                  e.stopPropagation();
                }
                e.currentTarget.blur();
                dispatch(elevioActions.resolution.setSolutionDropdownClosed());
              }
            }}
            input={{
              value: fieldValue,
              name: 'articleId',
              onChange: (e) => {
                if (e) setSearchTerm(e.currentTarget.value);
              },
              onFocus: (e) => {
                dispatch(elevioActions.resolution.setSolutionDropdownOpen());
                e.currentTarget.setSelectionRange(
                  // Sets the cursor to end of the input
                  articleSearchInputValue.length,
                  articleSearchInputValue.length,
                );
              },
              onBlur: () => {
                if (articleDropdownOpen) {
                  dispatch(
                    elevioActions.resolution.setSolutionDropdownClosed(),
                  );
                }
              },
            }}
            placeholder="Search for article..."
            icon="angle-down"
            autoComplete="off"
            noLabel={true}
          />
          <Dropdown
            fixed={true}
            isOpen={articleDropdownOpen}
            errorMessage={error ? 'There was an error' : undefined}
          >
            <ResolutionArticleList
              isFetching={isFetching}
              searchTerm={articleSearchInputValue}
              suggestions={suggestions}
            />
          </Dropdown>
        </Box>
      </Box>
    </Box>
  );
}

type ArticleListWrapperProps = {
  children: React.ReactNode;
};
export function ArticleListWrapper({
  children,
}: ArticleListWrapperProps): JSX.Element {
  return <div className={styles.suggestionPopupWrapper}>{children}</div>;
}
